import { useRef, useEffect, useState, type PropsWithChildren } from 'react';
import { Text, type TextT } from '@mentimeter/ragnar-ui/text';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import { VisualizationTextMarkupRenderer } from '@mentimeter/tiptap/visualization-text-markup-renderer';
import type { RenderChoiceLabel } from '../../../utils/types/render-options-by-slide-type/types';

const useIsTruncated = () => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, []);

  return { ref, isTruncated };
};

const ConditionalTooltipWrapper = ({
  tooltipText,
  children,
}: PropsWithChildren<{ tooltipText: string | undefined }>) => {
  if (tooltipText) {
    return (
      <Tooltip trigger={children} side="bottom" withPortal={false}>
        <Text fontSize="125" color="inherit">
          {tooltipText}
        </Text>
      </Tooltip>
    );
  }

  return children;
};

interface OptionLabelProps extends TextT {
  id: string;
  text: string;
  fontSize: number;
  renderChoiceLabel?: RenderChoiceLabel | undefined;
  choiceLabelsContainerClassName?: string | undefined;
}

export const OptionLabel = ({
  id,
  text,
  fontSize,
  children,
  renderChoiceLabel,
  choiceLabelsContainerClassName,
  ...rest
}: PropsWithChildren<OptionLabelProps>) => {
  const { ref, isTruncated } = useIsTruncated();

  const tooltipText = isTruncated ? text : undefined;

  const choiceLabel = <VisualizationTextMarkupRenderer content={text} />;

  const choiceLabelOverride = renderChoiceLabel?.({
    node: choiceLabel,
    data: {
      id,
      label: text,
    },
  });

  return (
    <ConditionalTooltipWrapper tooltipText={tooltipText}>
      <Text
        ref={ref}
        color="textWeak"
        fontSize={fontSize}
        lineHeight="none"
        fontWeight="regular"
        className={choiceLabelsContainerClassName}
        extend={() => ({
          '& div': {
            display: 'contents',
          },
          '& span': {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
            display: 'block',
          },
        })}
        {...rest}
      >
        {choiceLabelOverride ?? choiceLabel}
      </Text>
    </ConditionalTooltipWrapper>
  );
};
